import { PlayCircleOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { App, Button, Segmented, Space } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, describeError } from "../api/client";
import { useAuthStore } from "../auth/store";
import { EntityTable } from "../components/EntityTable";
import { PageHeader } from "../components/PageHeader";

const ACTIVE = ["ready", "received", "in_progress", "waiting", "problem"];

type View = "mine" | "free" | "done";

/**
 * N01: «Мои задания» — only what is assigned to the current worker, apart from the shared queue and from finished
 * work. «Взять следующее» hands out the most urgent free task of the worker's warehouse and queues.
 */
export function MyTasksPage() {
  const me = useAuthStore((s) => s.me);
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [view, setView] = useState<View>("mine");
  const userId = me?.user.id ?? "";

  const next = useMutation({
    mutationFn: () => api.post<{ task: { id: string; number: string } | null }>("/tasks/next", {}),
    onSuccess: (r) => {
      if (r.task) navigate(`/tasks/${r.task.id}`);
      else message.info("Свободных заданий в ваших очередях сейчас нет");
    },
    onError: (e) => message.error(describeError(e), 6),
  });

  const filters: Record<View, Record<string, unknown>> = {
    mine: { and: [{ field: "assignee_id", op: "eq", value: userId }, { field: "status", op: "in", value: ACTIVE }] },
    free: { and: [{ field: "assignee_id", op: "is_null" }, { field: "status", op: "eq", value: "ready" }] },
    done: { and: [{ field: "assignee_id", op: "eq", value: userId }, { field: "status", op: "eq", value: "done" }] },
  };

  return (
    <div>
      <PageHeader
        title="Мои задания"
        subtitle="Задания, назначенные вам; общая очередь и завершённые — на соседних вкладках"
        extra={
          can("tasks.execute") ? (
            <Button type="primary" size="large" icon={<PlayCircleOutlined />} loading={next.isPending} onClick={() => next.mutate()}>
              Взять следующее задание
            </Button>
          ) : undefined
        }
      />
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <Segmented<View>
          size="large"
          value={view}
          onChange={setView}
          options={[
            { value: "mine", label: "Мои текущие" },
            { value: "free", label: "Свободные в очереди" },
            { value: "done", label: "Завершённые мной" },
          ]}
        />
        <div className="wms-card" style={{ padding: 16 }}>
          <EntityTable key={view} entity="tasks" baseFilter={filters[view]} hideFields={view === "free" ? ["assignee_id"] : []} />
        </div>
      </Space>
    </div>
  );
}
